import { useCallback, useEffect, useState } from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { meetingAnalysis, requestMeetingAnalysis } from './api'
import type { MeetingAnalysis } from './types'

const POLL_MS = 8000

/**
 * The minutes.
 *
 * ## It is a request, not a render
 *
 * Nothing is generated until somebody asks. A meeting summarised automatically is a model
 * call nobody decided to make on a transcript of people speaking privately, and the cost of
 * being wrong is read by the people who were in the room.
 *
 * ## It says which model wrote it
 *
 * Minutes that look like minutes are trusted like minutes. The model name and the confidence
 * stay on screen so the reader knows what they are reading, and the full report lives in the
 * advisory console where the citations and the brief are.
 */
export function AnalysisPanel({
  meetingId, canHost,
}: { meetingId: string; canHost: boolean }) {
  const [analysis, setAnalysis] = useState<MeetingAnalysis | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const pull = useCallback(() => {
    meetingAnalysis(meetingId).then(setAnalysis).catch((e) => setError(explain(e)))
  }, [meetingId])

  useEffect(() => { pull() }, [pull])

  // Only while something is running: a finished run does not change, and a meeting nobody
  // has asked about does not need a timer at all.
  const running = analysis?.stateCode === 'QUEUED' || analysis?.stateCode === 'RUNNING'
  useEffect(() => {
    if (!running) return
    const t = setInterval(pull, POLL_MS)
    return () => clearInterval(t)
  }, [running, pull])

  const ask = async () => {
    setBusy(true)
    setError(null)
    try {
      setAnalysis(await requestMeetingAnalysis(meetingId))
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <ErrorStrip message={error} />

      {analysis?.headline && (
        <div className="space-y-2">
          <p className="font-medium">{analysis.headline}</p>
          {analysis.executiveSummary && (
            <p className="whitespace-pre-wrap text-erp-sm">{analysis.executiveSummary}</p>
          )}
          <p className="text-erp-xs text-muted-foreground">
            Written by {analysis.model ?? 'a model'} from the transcript
            {analysis.confidenceCode ? ` · confidence ${analysis.confidenceCode.toLowerCase()}` : ''}.
            Read it against the recording before it is relied on.
          </p>
        </div>
      )}

      {running && (
        <p className="flex items-center gap-2 text-erp-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          The council is reading the transcript. This takes a minute or two.
        </p>
      )}

      {analysis?.stateCode === 'FAILED' && (
        <Note tone="warn">
          The run failed. The transcript is unaffected — it can be read in the Transcript tab,
          and the minutes can be asked for again.
        </Note>
      )}

      {!analysis?.headline && !running && (
        <Note tone="info">
          No minutes yet. A summary is made from the transcript, on request — nothing is sent
          to a model until somebody asks for it.
        </Note>
      )}

      {canHost && !running && (
        <button type="button" className="erp-btn erp-btn-primary h-9 w-full text-erp-sm"
          disabled={busy} onClick={() => void ask()}>
          <Sparkles className="size-4" />
          {busy ? 'Asking…' : analysis?.headline ? 'Write them again' : 'Write the minutes'}
        </button>
      )}
      {!canHost && (
        <p className="text-erp-xs text-muted-foreground">
          Only the chair can ask for minutes.
        </p>
      )}
    </div>
  )
}
